import type { PagedResponse, ActivityLogEntry } from '../types/api'
import { isEntraConfigured } from '../auth'
import { createBearerHeaders, createHeaders, getApiKey } from './authHeaders'
import { ApiError, BearerRequiredError } from './apiError'

// Re-exported so existing callers of this module can reach the error type; anything that must stay
// importable without MSAL (utils/format.ts, tests) imports './apiError' directly instead.
export { ApiError, isApiError, BearerRequiredError, isBearerRequiredError } from './apiError'

/**
 * `fetch` options plus how to authenticate.
 *
 * `auth: 'bearer-only'` is for the endpoints the API pins to JwtBearer — every Observatory write (run a job,
 * clear a halt, handle a signal, cancel a run, acknowledge or restore a Data Comb finding) and the signal
 * assignee list. They refuse the shared API key with a 401 in every environment, so with no Entra token
 * the request is NOT SENT: `fetchApi` throws {@link BearerRequiredError} before calling `fetch`, and never
 * falls back to the key. Omitted, it is the usual Bearer-else-API-key. `src/conventions.test.ts` holds every
 * mutating call under `services/` to it.
 */
export type FetchApiOptions = RequestInit & { auth?: 'bearer-only' }

// API configuration - uses Vite proxy in development
const API_BASE_URL = '/api'

// Set API key in local storage
export function setApiKey(key: string): void {
  localStorage.setItem('lighthouse-api-key', key)
}

// Check if API key is configured (from env or local storage)
export function hasApiKey(): boolean {
  return !!getApiKey()
}

// Check if any authentication method is available
export function hasAuthentication(): boolean {
  return hasApiKey() || isEntraConfigured()
}

// Generic fetch wrapper with error handling.
// Exported so observatoryApi can reach the live endpoints through the same auth,
// credentials and error handling as every other call rather than a second client.
export async function fetchApi<T>(url: string, options?: FetchApiOptions): Promise<T> {
  const { auth, ...init } = options ?? {}

  let headers: HeadersInit
  if (auth === 'bearer-only') {
    const bearer = await createBearerHeaders()
    // Refused HERE, before fetch: the server would answer the key with a 401, and sending it would put the
    // shared secret on a request that cannot use it.
    if (bearer === null) throw new BearerRequiredError()
    headers = bearer
  } else {
    headers = await createHeaders()
  }

  const response = await fetch(url, {
    ...init,
    headers: {
      ...headers,
      ...init.headers,
    },
    // NO COOKIES. Auth on this surface is a header: Bearer from MSAL, or X-API-Key. The API's
    // MultiAuth policy scheme dispatches on `Authorization`, `X-API-Key` or the SignalR query token
    // and registers no cookie scheme at all, so a cookie authenticates nothing here.
    //
    // Sending them was not merely useless. **Cookies are scoped by host, not port**, so on localhost
    // `credentials: 'include'` attached every cookie any other dev app had set — the Hangfire
    // dashboard's chunked Entra sign-in cookie above all. Stacked on a multi-kilobyte Entra JWT that
    // pushed requests past the Vite dev server's 16 KB header ceiling, and every call came back
    // **431 Request Header Fields Too Large** — from Node's header parser, before Vite or the proxy
    // saw the request, which is why the failure looked like an API fault.
    //
    // This also drops the browser's Negotiate handshake for the SPA, which never worked anyway:
    // through the dev proxy the handshake spans a second connection, and from Static Web Apps the
    // call is cross-origin. The API keeps Negotiate for its non-browser intranet clients.
    credentials: 'omit',
  })

  if (!response.ok) {
    // Read the body BEFORE deciding the message. A 4xx from this API usually carries the actionable
    // detail -- which parameter was wrong, why a trigger was refused -- and reporting only the status
    // throws that away, leaving the user with "400 Bad Request" and nothing to act on.
    const raw = await response.text().catch(() => '')
    let message = ''
    let body: unknown = null
    let title: string | null = null
    let problemDetail: string | null = null
    if (raw) {
      try {
        body = JSON.parse(raw)
        if (typeof body === 'object' && body !== null && !Array.isArray(body)) {
          const record = body as Record<string, unknown>
          const field = (key: string) => (typeof record[key] === 'string' ? (record[key] as string) : null)
          title = field('title')
          problemDetail = field('detail')
          // DETAIL BEFORE TITLE: a ProblemDetails 400's title is the generic "One or more validation
          // errors occurred." and its detail is the reason, which is the part a user can act on.
          message = field('message') ?? problemDetail ?? title ?? ''
        } else if (typeof body === 'string') {
          // `BadRequest("...")` / `NotFound("...")` under a JSON formatter: the body is a JSON string,
          // and that string IS the reason.
          message = body
        }
      } catch {
        body = raw
        message = raw
      }
    }

    // The status travels with the error (ApiError), so a page can tell a 403 it should render as
    // restricted from a 404 or a 500, instead of reading every failure as the same red text.
    const fallback =
      response.status === 401
        ? 'Unauthorized - please sign in or check your API key'
        : response.status === 403
          ? 'Forbidden - you do not have access to this resource'
          : `API error: ${response.status} ${response.statusText}`
    throw new ApiError(response.status, message || fallback, body, title, problemDetail)
  }

  // Handle empty responses
  const text = await response.text()
  if (!text) {
    return {} as T
  }

  return JSON.parse(text)
}

// ===== Activity Log API =====

// Get recent activity log entries
export async function getActivityLog(
  pageNumber = 1,
  pageSize = 50,
  module?: string,
  eventType?: string
): Promise<PagedResponse<ActivityLogEntry>> {
  const params = new URLSearchParams({
    pageNumber: pageNumber.toString(),
    pageSize: pageSize.toString(),
  })
  if (module) params.append('module', module)
  if (eventType) params.append('eventType', eventType)

  return fetchApi<PagedResponse<ActivityLogEntry>>(
    `${API_BASE_URL}/activity?${params.toString()}`
  )
}
